/**
 * Notice a deploy from a tab opened before it (issue #35).
 *
 * A tab keeps running the bundle it loaded, so after a deploy it can show
 * behaviour (and wording) that no longer exists. The tab compares what it is
 * running with what the server would serve now:
 *
 *   - the web bundle: the hashed entry script named in a fresh index.html;
 *   - the API build: git_commit from /api/v1/version.
 *
 * Either changing means "FolioWorth was updated".
 */

export interface BuildFingerprint {
  /** Entry script path, e.g. /assets/index-BHV0fzLN.js. */
  entry: string | null;
  /** API git commit; null when unknown. */
  api: string | null;
}

/** The module entry script an index.html loads, or null. */
export function entryFromHtml(html: string): string | null {
  const m = html.match(/<script[^>]*type=["']module["'][^>]*src=["']([^"']+)["']/i)
    || html.match(/<script[^>]*src=["']([^"']+)["'][^>]*type=["']module["']/i);
  return m ? m[1] : null;
}

/** The entry script THIS tab is running. */
export function currentEntry(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src]');
  if (!el) return null;
  const src = el.getAttribute('src');
  return src ? src.replace(/^https?:\/\/[^/]+/, '') : null;
}

function normCommit(c?: string | null): string | null {
  const v = c?.trim();
  return v && v !== 'unknown' && v !== 'dev' ? v : null;
}

/**
 * True when what the server serves now differs from what this tab runs.
 * Unknowns never count as a change: a failed fetch must not nag.
 */
export function isUpdated(running: BuildFingerprint, latest: BuildFingerprint): boolean {
  if (running.entry && latest.entry && running.entry !== latest.entry) return true;
  const a = normCommit(running.api);
  const b = normCommit(latest.api);
  return Boolean(a && b && a !== b);
}

/** Reads what the server would serve a fresh tab now. Never cached. */
export async function fetchLatest(fetchFn: typeof fetch = fetch): Promise<BuildFingerprint> {
  const out: BuildFingerprint = { entry: null, api: null };
  try {
    const r = await fetchFn('/', { cache: 'no-store', headers: { Accept: 'text/html' } });
    if (r.ok) out.entry = entryFromHtml(await r.text());
  } catch { /* offline: unknown */ }
  try {
    const r = await fetchFn('/api/v1/version', { cache: 'no-store' });
    if (r.ok) out.api = normCommit(((await r.json()) as { git_commit?: string }).git_commit);
  } catch { /* unknown */ }
  return out;
}

/** How often an open tab checks, besides on focus. */
export const VERSION_POLL_MS = 5 * 60 * 1000;
